import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Inbox, Send, Search, AtSign, Shield, Eye, Trash2, MailOpen, Check, CheckCheck,
  Bell, BellOff, X, Dot, RefreshCw, ChevronRight, Clock, CircleAlert,
  Flag, Plus, Mail, ShieldAlert, Paperclip, Filter, EyeOff, UsersRound,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useNotifications } from '@/lib/notifications';
import { formatDateTime } from '@/lib/format';
import { toast } from 'sonner';
import { useCompose } from '@/lib/compose';
import MessageViewerDialog from '@/components/mail/MessageViewerDialog';
import MailGroupsManager from '@/components/mail/MailGroupsManager';
import { DigestView } from '@/components/digest/DigestView';

const MENTION_TYPES = ['comment_mention', 'order_comment', 'order_comment_sa'];

// Is this inbox row a SYSTEM notification? (i.e. not a @mention and not an
// internal mail) — mirrors the "System" quick-filter definition.
const isSystemNotif = (n) => !MENTION_TYPES.includes(n.type) && n.type !== 'internal_message';

// Derive a stable, human-readable "subject" for a SYSTEM notification from its
// title. System titles follow a "Subject · <dynamic> · <extra>" shape — e.g.
// "Procure Gold · OF-…", "Delayed · … · Order Received", "Diamond requirement
// raised" — so the leading segment before the middle-dot is a clean subject to
// group by ("Procure Gold", "Delayed", "Diamond requirement raised").
const systemSubjectOf = (n) => {
  // Daily Activity Digest notifications always group under one stable subject
  // so the System filter can isolate them regardless of the date in the title.
  if (n.type === 'daily_digest') return 'Daily Digest';
  const raw = (n.title || n.subject || '').trim();
  if (!raw) return 'Other';
  const first = raw.split('·')[0].trim();
  return first || raw;
};

function initials(name) {
  const s = (name || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || s[0].toUpperCase();
}

function InitialsAvatar({ name, tone = 'blue', className = '' }) {
  const tones = {
    blue: 'bg-[var(--diamond-bg)] text-[var(--diamond-icon)] border-[var(--diamond-border)]',
    slate: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span
      className={`inline-flex items-center justify-center h-7 w-7 rounded-full border text-[10px] font-semibold ${tones[tone]} ${className}`}
      title={name}
    >
      {initials(name)}
    </span>
  );
}

function TickIcon({ state, size = 16 }) {
  // sent -> single grey; delivered -> double grey; read -> double blue
  const label = state === 'read' ? 'Read by all' : state === 'delivered' ? 'Delivered' : 'Sent';
  const cls = state === 'read' ? 'text-[var(--diamond-icon)]' : 'text-slate-400';
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex ${cls}`} data-testid="notifications-sent-ticks" aria-label={label}>
            {state === 'sent' ? <Check size={size} /> : <CheckCheck size={size} />}
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const {
    inboxUnread, sentUnread, wsLive, lastEventAt,
    soundEnabled, setSoundEnabled, refreshCounts, setInboxUnread,
  } = useNotifications();

  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ['inbox', 'sent', 'priority', 'groups', ...(isSuperAdmin ? ['audit'] : [])];
  const rawTab = searchParams.get('tab');
  const tab = validTabs.includes(rawTab) ? rawTab : 'inbox';
  const setTab = (t) => setSearchParams((p) => { p.set('tab', t); return p; }, { replace: true });

  // ---- Internal mail state ----
  // Compose is provided globally (so a minimised draft survives navigation);
  // this page just triggers it and refreshes on the `mail:sent` event.
  const { openCompose } = useCompose();
  const [viewer, setViewer] = useState({ open: false, messageId: null, admin: false, disappearing: false });
  const [emailSent, setEmailSent] = useState([]);       // /mail/sent
  const [priorityData, setPriorityData] = useState({ received: [], sent: [] });
  const [auditRows, setAuditRows] = useState([]);
  const [auditFilters, setAuditFilters] = useState({ sender_id: 'all', recipient_id: 'all', subject: '', attachments: 'all', disappearing: 'all', deleted: 'all', date_from: '', date_to: '' });
  const [auditPeople, setAuditPeople] = useState([]);
  // Wave 4.x — Sent-tab "Disappearing Mail" quick filter.
  const [sentQuick, setSentQuick] = useState('all');
  // Wave 4.x — Super Admin "hard delete" (no-trace) confirmation target.
  const [hardDelete, setHardDelete] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(false);

  const openViewer = (messageId, admin = false, disappearing = false) => setViewer({ open: true, messageId, admin, disappearing });
  const closeViewer = () => {
    const wasDisappearing = viewer.disappearing;
    const wasAdmin = viewer.admin;
    setViewer({ open: false, messageId: null, admin: false, disappearing: false });
    // A recipient who just read a viewable-once mail no longer has it — pull
    // it out of their inbox / priority views right away.
    if (wasDisappearing && !wasAdmin) {
      if (tab === 'inbox') loadInbox();
      else if (tab === 'priority') loadPriority();
      refreshCounts();
    }
  };
  const replyTo = (m) => openCompose({
    recipients: [{ id: m.sender?.user_id, name: m.sender?.name, email: m.sender?.email }],
    subject: /^re:/i.test(m.subject || '') ? m.subject : `Re: ${m.subject || ''}`,
    parent_id: m.message_id, reply_kind: 'reply',
  });
  const replyAll = (m) => {
    const others = (m.recipients || [])
      .filter((r) => r.user_id !== user?.id)
      .map((r) => ({ id: r.user_id, name: r.name, email: r.email }));
    const list = [{ id: m.sender?.user_id, name: m.sender?.name, email: m.sender?.email }, ...others]
      .filter((r) => r.id && r.id !== user?.id);
    const seen = new Set(); const dedup = [];
    list.forEach((r) => { if (!seen.has(r.id)) { seen.add(r.id); dedup.push(r); } });
    openCompose({ recipients: dedup, subject: /^re:/i.test(m.subject || '') ? m.subject : `Re: ${m.subject || ''}`, parent_id: m.message_id, reply_kind: 'reply_all' });
  };

  const [inboxItems, setInboxItems] = useState([]);
  const [sentItems, setSentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [filterOptions, setFilterOptions] = useState({ senders: [], orders: [] });
  const [receipt, setReceipt] = useState(null); // sent item for the receipt dialog
  // Daily Digest viewer dialog.
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestNotif, setDigestNotif] = useState(null);

  const [filters, setFilters] = useState({
    query: '', orderNo: '', partyId: 'all', dateFrom: '', dateTo: '', unreadOnly: false, quick: 'all',
  });
  // Inbox → System filter: the selected system-message subject to narrow to
  // ('all' = every system subject). Reset whenever we leave the System filter.
  const [subjectFilter, setSubjectFilter] = useState('all');

  const resetSelection = () => setSelectedIds(new Set());

  // ---- Loaders --------------------------------------------------------
  const loadInbox = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = { limit: 300 };
      if (filters.partyId && filters.partyId !== 'all') params.sender_id = filters.partyId;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.unreadOnly) params.unread_only = true;
      const { data } = await api.get('/notifications/inbox', { params });
      setInboxItems(Array.isArray(data) ? data : []);
      // Fetching inbox marks items delivered — refresh badges.
      refreshCounts();
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [filters.partyId, filters.dateFrom, filters.dateTo, filters.unreadOnly, refreshCounts]);

  const loadSent = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = { limit: 300 };
      if (filters.partyId && filters.partyId !== 'all') params.recipient_id = filters.partyId;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.unreadOnly) params.unread_only = true;
      const [cRes, mRes] = await Promise.all([
        api.get('/notifications/sent', { params }),
        api.get('/mail/sent', { params: { limit: 300 } }).catch(() => ({ data: [] })),
      ]);
      setSentItems(Array.isArray(cRes.data) ? cRes.data : []);
      setEmailSent(Array.isArray(mRes.data) ? mRes.data : []);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [filters.partyId, filters.dateFrom, filters.dateTo, filters.unreadOnly]);

  const loadPriority = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const { data } = await api.get('/mail/priority');
      setPriorityData({ received: data.received || [], sent: data.sent || [] });
    } catch { setError(true); }
    finally { setLoading(false); }
  }, []);

  const loadAudit = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = {};
      if (auditFilters.sender_id !== 'all') params.sender_id = auditFilters.sender_id;
      if (auditFilters.recipient_id !== 'all') params.recipient_id = auditFilters.recipient_id;
      if (auditFilters.subject.trim()) params.subject = auditFilters.subject.trim();
      if (auditFilters.attachments !== 'all') params.has_attachments = auditFilters.attachments === 'yes';
      if (auditFilters.disappearing !== 'all') params.disappearing = auditFilters.disappearing === 'yes';
      if (auditFilters.deleted !== 'all') params.deleted = auditFilters.deleted === 'yes';
      if (auditFilters.date_from) params.date_from = auditFilters.date_from;
      if (auditFilters.date_to) params.date_to = auditFilters.date_to;
      const { data } = await api.get('/mail/admin/all', { params });
      setAuditRows(Array.isArray(data) ? data : []);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [auditFilters]);

  // Req#1 — Quick date-range presets for the covert audit tab. Sets both
  // date_from/date_to to the last N days (inclusive of today) as yyyy-mm-dd
  // bounds that the backend interprets as IST day boundaries.
  const applyAuditPreset = useCallback((days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setAuditFilters((f) => ({ ...f, date_from: fmt(start), date_to: fmt(end) }));
  }, []);

  const loadAuditPeople = useCallback(async () => {
    if (!isSuperAdmin) return;
    try { const { data } = await api.get('/mail/admin/filters'); setAuditPeople(data.people || []); }
    catch { /* noop */ }
  }, [isSuperAdmin]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications/inbox/filters');
      setFilterOptions({ senders: data.senders || [], orders: data.orders || [] });
    } catch {}
  }, []);

  useEffect(() => { loadFilterOptions(); loadAuditPeople(); }, [loadFilterOptions, loadAuditPeople]);
  useEffect(() => {
    resetSelection();
    if (tab === 'inbox') loadInbox();
    else if (tab === 'sent') loadSent();
    else if (tab === 'priority') loadPriority();
    else if (tab === 'audit') loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, loadInbox, loadSent, loadPriority]);

  // Reload audit when its filters change (SA tab active).
  useEffect(() => {
    if (tab === 'audit') loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditFilters]);

  // Deep-link: /notifications?msgId=... opens the message viewer directly.
  useEffect(() => {
    const mid = searchParams.get('msgId');
    if (mid) {
      openViewer(mid, false);
      setSearchParams((p) => { p.delete('msgId'); return p; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link: /notifications?tab=inbox&filter=priority|disappearing|… presets
  // the inbox quick filter (used by the Dashboard notification chips).
  useEffect(() => {
    const f = searchParams.get('filter');
    const valid = ['all', 'unread', 'priority', 'mentions', 'mails', 'disappearing', 'system'];
    if (f && valid.includes(f)) {
      setFilters((prev) => ({ ...prev, quick: f }));
      setSearchParams((p) => { p.delete('filter'); return p; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Realtime: refetch current tab when a new notification arrives.
  useEffect(() => {
    if (!lastEventAt) return;
    if (tab === 'inbox') loadInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEventAt]);

  // ---- Client-side derived lists (search + quick filters) -------------
  const q = filters.query.trim().toLowerCase();
  const orderNoQ = filters.orderNo.trim().toLowerCase();

  const visibleInbox = useMemo(() => {
    const filtered = inboxItems.filter((n) => {
      if (filters.quick === 'unread' && n.read) return false;
      if (filters.quick === 'priority' && !n.priority) return false;
      if (filters.quick === 'mentions' && !MENTION_TYPES.includes(n.type)) return false;
      if (filters.quick === 'mails' && n.type !== 'internal_message') return false;
      if (filters.quick === 'disappearing' && !n.disappearing) return false;
      if (filters.quick === 'system' && (MENTION_TYPES.includes(n.type) || n.type === 'internal_message')) return false;
      if (filters.quick === 'system' && subjectFilter !== 'all' && systemSubjectOf(n) !== subjectFilter) return false;
      if (orderNoQ && !((n.order_no || '').toLowerCase().includes(orderNoQ))) return false;
      if (q) {
        const hay = `${n.title || ''} ${n.message || ''} ${n.subject || ''} ${n.sender_name || ''} ${n.order_no || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // Wave 3.99ae — Pin UNREAD priority items to the very top (they stay red +
    // bold there until read). Everything else keeps newest-first order.
    const rank = (n) => (n.priority && !n.read ? 0 : 1);
    return filtered
      .map((n, i) => ({ n, i }))
      .sort((a, b) => (rank(a.n) - rank(b.n)) || (a.i - b.i))
      .map((x) => x.n);
  }, [inboxItems, filters.quick, subjectFilter, q, orderNoQ]);

  // System-message subjects with unread + total counts, powering the
  // Inbox → System subject combo box. Grouped by the derived subject
  // (e.g. "Delayed", "Procure Gold", "Diamond requirement raised"), sorted
  // so the biggest unread piles surface first — the operator can jump to a
  // subject and not miss important system messages.
  const systemSubjects = useMemo(() => {
    const map = new Map();
    let unreadTotal = 0;
    for (const n of inboxItems) {
      if (!isSystemNotif(n)) continue;
      const s = systemSubjectOf(n);
      const cur = map.get(s) || { subject: s, total: 0, unread: 0 };
      cur.total += 1;
      if (!n.read) { cur.unread += 1; unreadTotal += 1; }
      map.set(s, cur);
    }
    const list = Array.from(map.values()).sort(
      (a, b) => (b.unread - a.unread) || (b.total - a.total) || a.subject.localeCompare(b.subject),
    );
    return { list, unreadTotal };
  }, [inboxItems]);

  // Reset the subject narrow-down whenever we leave the System quick-filter.
  useEffect(() => {
    if (filters.quick !== 'system' && subjectFilter !== 'all') setSubjectFilter('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.quick]);

  // Per-category counts (whole inbox, independent of the active quick filter)
  // — powers the count badges on the filter chips for at-a-glance triage.
  const quickCounts = useMemo(() => {
    const c = { all: inboxItems.length, unread: 0, priority: 0, mentions: 0, mails: 0, disappearing: 0, system: 0 };
    for (const n of inboxItems) {
      if (!n.read) c.unread += 1;
      if (n.priority) c.priority += 1;
      if (n.disappearing) c.disappearing += 1;
      if (MENTION_TYPES.includes(n.type)) c.mentions += 1;
      else if (n.type === 'internal_message') c.mails += 1;
      else c.system += 1;
    }
    return c;
  }, [inboxItems]);

  const visibleSent = useMemo(() => {
    const comments = sentItems.map((s) => ({ ...s, kind: 'comment' }));
    const emails = emailSent.map((s) => ({ ...s, kind: 'email' }));
    const all = [...comments, ...emails].filter((s) => {
      if (sentQuick === 'disappearing' && !(s.kind === 'email' && s.disappearing)) return false;
      if (orderNoQ && !((s.order_no || '').toLowerCase().includes(orderNoQ))) return false;
      if (q) {
        const hay = `${s.body || ''} ${s.subject || ''} ${s.order_no || ''} ${(s.recipients || []).map((r) => r.name).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    all.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    return all;
  }, [sentItems, emailSent, q, orderNoQ, sentQuick]);

  // Wave 4.x — Count of disappearing mails in the sender's Sent box (chip badge).
  const sentDisappearingCount = useMemo(() => emailSent.filter((s) => s.disappearing).length, [emailSent]);

  // Recipients across sent items -> for the "recipient" filter dropdown.
  const sentRecipients = useMemo(() => {
    const m = new Map();
    sentItems.forEach((s) => (s.recipients || []).forEach((r) => { if (r.user_id && !m.has(r.user_id)) m.set(r.user_id, r.name); }));
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [sentItems]);

  // ---- Actions --------------------------------------------------------
  const openInboxItem = async (n) => {
    // Daily Digest → open the rich company-wise summary dialog (marks read).
    if (n.type === 'daily_digest') {
      if (!n.read) {
        setInboxItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setInboxUnread((u) => Math.max(0, u - 1));
        try { await api.post(`/notifications/${n.id}/read`); } catch {}
        refreshCounts();
      }
      setDigestNotif(n);
      setDigestOpen(true);
      return;
    }
    // Internal email → open the message viewer (marks read inside the viewer).
    if (n.type === 'internal_message' && n.message_id) {
      if (!n.read) {
        setInboxItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setInboxUnread((u) => Math.max(0, u - 1));
      }
      openViewer(n.message_id, false, !!n.disappearing);
      return;
    }
    // Optimistic mark-read + navigate to the related order form.
    if (!n.read) {
      setInboxItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setInboxUnread((u) => Math.max(0, u - 1));
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
      refreshCounts();
    }
    if (n.link) navigate(n.link);
    else if (n.entity_type === 'order' && n.entity_id) navigate(`/orders/${n.entity_id}`);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = visibleInbox.length > 0 && visibleInbox.every((n) => selectedIds.has(n.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) resetSelection();
    else setSelectedIds(new Set(visibleInbox.map((n) => n.id)));
  };

  const bulkMarkRead = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setInboxItems((prev) => prev.map((x) => (selectedIds.has(x.id) ? { ...x, read: true } : x)));
    resetSelection();
    try { await api.post('/notifications/bulk/read', { ids }); toast.success(`Marked ${ids.length} as read`); }
    catch { toast.error('Failed to mark read'); }
    refreshCounts();
  };

  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setInboxItems((prev) => prev.filter((x) => !selectedIds.has(x.id)));
    resetSelection();
    try { await api.post('/notifications/bulk/delete', { ids }); toast.success(`Deleted ${ids.length} notification${ids.length === 1 ? '' : 's'}`); }
    catch { toast.error('Failed to delete'); }
    refreshCounts();
  };

  const clearFilters = () => setFilters({ query: '', orderNo: '', partyId: 'all', dateFrom: '', dateTo: '', unreadOnly: false, quick: 'all' });

  // ---- Single-row (hover) actions -------------------------------------
  const markOneRead = async (n) => {
    if (n.read) return;
    setInboxItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setInboxUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${n.id}/read`); }
    catch { toast.error('Failed to mark as read'); }
    refreshCounts();
  };

  const deleteOne = async (n) => {
    setInboxItems((prev) => prev.filter((x) => x.id !== n.id));
    setSelectedIds((prev) => { const next = new Set(prev); next.delete(n.id); return next; });
    try { await api.post('/notifications/bulk/delete', { ids: [n.id] }); toast.success('Notification deleted'); }
    catch { toast.error('Failed to delete'); }
    refreshCounts();
  };

  // Priority folder: delete an item. Received items are removed from the
  // caller's mailbox; sent emails open the viewer (which offers the sender's
  // self / everyone choice). The priority marking is never removed.
  const deletePriorityItem = async (it) => {
    try {
      if (it.direction === 'received') {
        if (it.kind === 'email' && it.message_id) {
          await api.delete(`/mail/${it.message_id}`, { params: { scope: 'self' } });
        } else if (it.notif_id) {
          await api.post('/notifications/bulk/delete', { ids: [it.notif_id] });
        }
        toast.success('Removed from your mailbox');
        loadPriority();
        refreshCounts();
        return;
      }
      // sent
      if (it.kind === 'email' && it.message_id) { openViewer(it.message_id, false); return; }
      if (it.link) navigate(it.link);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const onMailChanged = () => {
    if (tab === 'inbox') loadInbox();
    else if (tab === 'sent') loadSent();
    else if (tab === 'priority') loadPriority();
    else if (tab === 'audit') loadAudit();
    refreshCounts();
  };

  // A mail sent from the GLOBAL compose window (possibly while this page was in
  // the background) fires a `mail:sent` event — refresh the active folder.
  useEffect(() => {
    const handler = () => onMailChanged();
    window.addEventListener('mail:sent', handler);
    return () => window.removeEventListener('mail:sent', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Wave 4.x — Super Admin "hard delete": permanently erase an internal
  // message + every related inbox/receipt row (no trace anywhere).
  const doHardDelete = async () => {
    if (!hardDelete) return;
    setHardDeleting(true);
    try {
      await api.delete(`/mail/admin/${hardDelete.message_id}`);
      toast.success('Message permanently erased — no trace left');
      setHardDelete(null);
      loadAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to erase message');
    } finally {
      setHardDeleting(false);
    }
  };


  const openReceipt = async (s) => {
    setReceipt(s);
    // Refresh receipts for accuracy.
    try {
      const { data } = await api.get(`/notifications/sent/${s.comment_id}/receipts`);
      setReceipt((cur) => (cur && cur.comment_id === s.comment_id ? { ...cur, recipients: data.recipients, tick: data.tick } : cur));
    } catch {}
  };

  // ---- Render helpers -------------------------------------------------
  // Compact horizontal quick-filter chip (Inbox only). Replaces the old
  // left-rail folders/filters so the list reclaims full page width. An
  // optional count badge turns the row into an at-a-glance triage bar.
  const QuickChip = ({ icon: Icon, label, active, onClick, testid, count, tone = 'gold' }) => {
    const toneActive = tone === 'rose'
      ? 'bg-rose-50 border-rose-200 text-rose-700'
      : tone === 'sky'
        ? 'bg-[var(--diamond-bg)] border-[var(--diamond-icon)]/30 text-[var(--ink-navy)]'
        : 'bg-[var(--gold-bg)] border-[var(--gold-accent-soft)] text-[var(--ink-navy)]';
    const iconActive = tone === 'rose' ? 'text-rose-500' : tone === 'sky' ? 'text-[var(--diamond-icon)]' : 'text-[var(--gold-icon)]';
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        aria-pressed={active}
        className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full border text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] ${
          active
            ? toneActive
            : 'bg-white border-[var(--divider)] text-slate-500 hover:bg-[var(--paper-warm)] hover:text-[var(--ink-navy)]'
        }`}
      >
        <Icon size={13} className={active ? iconActive : 'text-slate-400'} />
        {label}
        {count > 0 && (
          <span
            className={`ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-semibold tabular-nums ${
              active ? 'bg-white/70 text-[var(--ink-navy)]' : 'bg-[var(--paper-warm)] text-slate-500'
            }`}
            data-testid={`${testid}-count`}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
    );
  };

  return (
    <AppShell title="Notifications" subtitle="Triage updates and track your @mention receipts">
      <div className="w-full" data-testid="notifications-page">
        {/* Main column — full width (left rail removed to reclaim horizontal space) */}
        <div className="min-w-0">
          {/* Header row */}
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${wsLive ? 'bg-emerald-500' : 'bg-slate-300'}`} title={wsLive ? 'Live' : 'Connecting…'} data-testid="notifications-ws-status" />
              <span className="text-xs text-slate-500">{wsLive ? 'Live' : 'Reconnecting…'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button className="luxury-button h-9" onClick={() => openCompose(null)} data-testid="mail-compose-open">
                <Plus size={15} className="mr-1.5" /> Compose
              </Button>
              {soundEnabled ? <Bell size={15} className="text-[var(--ink-navy)]" /> : <BellOff size={15} className="text-slate-400" />}
              <span className="text-sm text-slate-600">Sound</span>
              <Switch checked={soundEnabled} onCheckedChange={setSoundEnabled} data-testid="notifications-sound-toggle" />
              <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500" onClick={() => { if (tab === 'inbox') loadInbox(); else if (tab === 'sent') loadSent(); else if (tab === 'priority') loadPriority(); else if (tab === 'audit') loadAudit(); }} data-testid="notifications-refresh" title="Refresh">
                <RefreshCw size={14} />
              </Button>
            </div>
          </div>

          {/* Tabs + quick filters (chips reclaim the space the old left rail used) */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="inline-flex rounded-xl border bg-white p-1 h-auto">
                <TabsTrigger value="inbox" data-testid="notifications-tab-inbox" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-[var(--diamond-bg)] data-[state=active]:text-[var(--ink-navy)]">
                  <Inbox size={15} className="mr-1.5" /> Inbox
                  {inboxUnread > 0 && <Badge className="ml-2 bg-rose-600 text-white text-[10px] px-1.5">{inboxUnread > 99 ? '99+' : inboxUnread}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="sent" data-testid="notifications-tab-sent" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-[var(--diamond-bg)] data-[state=active]:text-[var(--ink-navy)]">
                  <Send size={15} className="mr-1.5" /> Sent
                  {sentUnread > 0 && <Badge className="ml-2 bg-[var(--diamond-icon)] text-white text-[10px] px-1.5">{sentUnread > 99 ? '99+' : sentUnread}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="priority" data-testid="notifications-tab-priority" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-rose-50 data-[state=active]:text-rose-700">
                  <Flag size={15} className="mr-1.5" /> Priority
                </TabsTrigger>
                <TabsTrigger value="groups" data-testid="notifications-tab-groups" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-[var(--diamond-bg)] data-[state=active]:text-[var(--ink-navy)]">
                  <UsersRound size={15} className="mr-1.5" /> Groups
                </TabsTrigger>
                {isSuperAdmin && (
                  <TabsTrigger value="audit" data-testid="notifications-tab-audit" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-amber-50 data-[state=active]:text-amber-800">
                    <ShieldAlert size={15} className="mr-1.5" /> Internal Mail
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
            {tab === 'inbox' && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="notifications-quick-filters">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mr-0.5 hidden sm:inline">Filter</span>
                <QuickChip icon={Inbox} label="All" tone="sky" active={filters.quick === 'all'} count={quickCounts.all} onClick={() => setFilters((f) => ({ ...f, quick: 'all' }))} testid="notifications-rail-filter-all" />
                <QuickChip icon={Dot} label="Unread" active={filters.quick === 'unread'} count={quickCounts.unread} onClick={() => setFilters((f) => ({ ...f, quick: f.quick === 'unread' ? 'all' : 'unread' }))} testid="notifications-rail-filter-unread" />
                <QuickChip icon={Flag} label="Priority" tone="rose" active={filters.quick === 'priority'} count={quickCounts.priority} onClick={() => setFilters((f) => ({ ...f, quick: f.quick === 'priority' ? 'all' : 'priority' }))} testid="notifications-rail-filter-priority" />
                <QuickChip icon={AtSign} label="Mentions" active={filters.quick === 'mentions'} count={quickCounts.mentions} onClick={() => setFilters((f) => ({ ...f, quick: f.quick === 'mentions' ? 'all' : 'mentions' }))} testid="notifications-rail-filter-mentions" />
                <QuickChip icon={Mail} label="Mails" active={filters.quick === 'mails'} count={quickCounts.mails} onClick={() => setFilters((f) => ({ ...f, quick: f.quick === 'mails' ? 'all' : 'mails' }))} testid="notifications-rail-filter-mails" />
                <QuickChip icon={EyeOff} label="Viewable Once" tone="rose" active={filters.quick === 'disappearing'} count={quickCounts.disappearing} onClick={() => setFilters((f) => ({ ...f, quick: f.quick === 'disappearing' ? 'all' : 'disappearing' }))} testid="notifications-rail-filter-disappearing" />
                <QuickChip icon={Shield} label="System" active={filters.quick === 'system'} count={quickCounts.system} onClick={() => setFilters((f) => ({ ...f, quick: f.quick === 'system' ? 'all' : 'system' }))} testid="notifications-rail-filter-system" />
              </div>
            )}
            {tab === 'sent' && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="notifications-sent-filters">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mr-0.5 hidden sm:inline">Filter</span>
                <QuickChip icon={Send} label="All" tone="sky" active={sentQuick === 'all'} onClick={() => setSentQuick('all')} testid="notifications-sent-filter-all" />
                <QuickChip icon={EyeOff} label="Disappearing Mail" tone="rose" active={sentQuick === 'disappearing'} count={sentDisappearingCount} onClick={() => setSentQuick(sentQuick === 'disappearing' ? 'all' : 'disappearing')} testid="notifications-sent-filter-disappearing" />
              </div>
            )}
          </div>

          {/* Groups tab — personal recipient groups manager */}
          {tab === 'groups' && <MailGroupsManager />}

          {/* List panel */}
          {tab !== 'groups' && (
          <Card className="rounded-xl border bg-white overflow-hidden mt-4">
            {/* Filter bar (Inbox + Sent only) */}
            {(tab === 'inbox' || tab === 'sent') && (
            <div className="px-3 py-2.5 border-b border-[var(--divider)] bg-white">
              {/* Inbox → System: jump to a specific system-message subject.
                  System sends many messages ("Delayed", "Procure Gold",
                  "<Material> requirement raised", …); this combo lists each
                  subject with its UNREAD count so nothing important is missed. */}
              {tab === 'inbox' && filters.quick === 'system' && systemSubjects.list.length > 0 && (
                <div className="mb-2.5 flex items-center gap-2 flex-wrap" data-testid="notifications-system-subject-bar">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <Shield size={13} className="text-slate-400" /> System subject
                  </span>
                  <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                    <SelectTrigger className="h-9 w-[280px]" data-testid="notifications-system-subject-select">
                      <SelectValue placeholder="All system subjects" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[340px]">
                      <SelectItem value="all">
                        <span className="inline-flex items-center gap-2">
                          <span>All system subjects</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 text-slate-500 border-slate-200">
                            {systemSubjects.unreadTotal} unread
                          </Badge>
                        </span>
                      </SelectItem>
                      {systemSubjects.list.map((s) => (
                        <SelectItem
                          key={s.subject}
                          value={s.subject}
                          data-testid={`notifications-system-subject-option-${s.subject.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                          <span className="inline-flex items-center gap-2 pr-1">
                            <span className="truncate max-w-[190px]">{s.subject}</span>
                            {s.unread > 0 ? (
                              <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[10px] px-1.5">
                                {s.unread}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] px-1.5 text-slate-400 border-slate-200">
                                0
                              </Badge>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {subjectFilter !== 'all' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 px-2 text-slate-500"
                      onClick={() => setSubjectFilter('all')}
                      data-testid="notifications-system-subject-clear"
                    >
                      <X size={14} className="mr-1" /> All subjects
                    </Button>
                  )}
                  <span className="text-[11px] text-slate-400 hidden md:inline">Pick a subject to focus — unread counts shown beside each</span>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[180px]">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={filters.query}
                    onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
                    placeholder="Search notifications…"
                    className="pl-8 h-9"
                    data-testid="notifications-search-input"
                  />
                </div>
                <Input
                  value={filters.orderNo}
                  onChange={(e) => setFilters((f) => ({ ...f, orderNo: e.target.value }))}
                  placeholder="Order #"
                  className="h-9 w-[130px]"
                  data-testid="notifications-filter-order"
                />
                <Select value={filters.partyId} onValueChange={(v) => setFilters((f) => ({ ...f, partyId: v }))}>
                  <SelectTrigger className="h-9 w-[170px]" data-testid="notifications-filter-party">
                    <SelectValue placeholder={tab === 'inbox' ? 'Any sender' : 'Any recipient'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{tab === 'inbox' ? 'Any sender' : 'Any recipient'}</SelectItem>
                    {(tab === 'inbox' ? filterOptions.senders : sentRecipients).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input type="date" value={filters.dateFrom} onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))} className="h-9 w-[150px]" data-testid="notifications-filter-date-from" title="From date" />
                <Input type="date" value={filters.dateTo} onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))} className="h-9 w-[150px]" data-testid="notifications-filter-date-to" title="To date" />
                <label className="flex items-center gap-2 text-sm text-slate-600 pl-1">
                  <Switch checked={filters.unreadOnly} onCheckedChange={(v) => setFilters((f) => ({ ...f, unreadOnly: v }))} data-testid="notifications-filter-unread-only" />
                  Unread
                </label>
                <Button variant="ghost" size="sm" className="h-9 px-2 text-slate-500" onClick={clearFilters} data-testid="notifications-clear-filters">
                  <X size={14} className="mr-1" /> Clear
                </Button>
              </div>

              {/* Bulk action bar (Inbox only) */}
              {tab === 'inbox' && selectedIds.size > 0 && (
                <div className="mt-2 flex items-center gap-2 animate-in fade-in slide-in-from-top-1 duration-200" data-testid="notifications-bulk-action-bar">
                  <span className="text-sm font-medium text-[var(--ink-navy)]">{selectedIds.size} selected</span>
                  <Button size="sm" className="luxury-button h-8" onClick={bulkMarkRead} data-testid="notifications-bulk-mark-read-button">
                    <MailOpen size={14} className="mr-1.5" /> Mark read
                  </Button>
                  <Button size="sm" variant="destructive" className="h-8" onClick={bulkDelete} data-testid="notifications-bulk-delete-button">
                    <Trash2 size={14} className="mr-1.5" /> Delete
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={resetSelection} data-testid="notifications-bulk-clear-button">
                    Clear selection
                  </Button>
                </div>
              )}

              {/* Select-all row (Inbox) */}
              {tab === 'inbox' && !loading && visibleInbox.length > 0 && (
                <div className="mt-2 flex items-center gap-2 pl-1">
                  <Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll} data-testid="notifications-select-all" />
                  <span className="text-xs text-slate-500">Select all ({visibleInbox.length})</span>
                </div>
              )}
            </div>
            )}

            {/* Super Admin audit filter bar */}
            {tab === 'audit' && (
              <div className="px-3 py-2.5 border-b border-[var(--divider)] bg-amber-50/40" data-testid="mail-audit-filters">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative flex-1 min-w-[180px]">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input value={auditFilters.subject} onChange={(e) => setAuditFilters((f) => ({ ...f, subject: e.target.value }))} placeholder="Search subject…" className="pl-8 h-9" data-testid="mail-audit-subject" />
                  </div>
                  <Select value={auditFilters.sender_id} onValueChange={(v) => setAuditFilters((f) => ({ ...f, sender_id: v }))}>
                    <SelectTrigger className="h-9 w-[170px]" data-testid="mail-audit-sender"><SelectValue placeholder="Any sender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any sender</SelectItem>
                      {auditPeople.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Select value={auditFilters.recipient_id} onValueChange={(v) => setAuditFilters((f) => ({ ...f, recipient_id: v }))}>
                    <SelectTrigger className="h-9 w-[170px]" data-testid="mail-audit-recipient"><SelectValue placeholder="Any recipient" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any recipient</SelectItem>
                      {auditPeople.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Select value={auditFilters.attachments} onValueChange={(v) => setAuditFilters((f) => ({ ...f, attachments: v }))}>
                    <SelectTrigger className="h-9 w-[150px]" data-testid="mail-audit-attachments"><SelectValue placeholder="Attachments" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any attachments</SelectItem>
                      <SelectItem value="yes">Has attachments</SelectItem>
                      <SelectItem value="no">No attachments</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={auditFilters.disappearing} onValueChange={(v) => setAuditFilters((f) => ({ ...f, disappearing: v }))}>
                    <SelectTrigger className="h-9 w-[160px]" data-testid="mail-audit-disappearing"><SelectValue placeholder="Disappearing" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any mail type</SelectItem>
                      <SelectItem value="yes">Disappearing mails</SelectItem>
                      <SelectItem value="no">Regular mails</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={auditFilters.deleted} onValueChange={(v) => setAuditFilters((f) => ({ ...f, deleted: v }))}>
                    <SelectTrigger className="h-9 w-[150px]" data-testid="mail-audit-deleted"><SelectValue placeholder="Deleted" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any status</SelectItem>
                      <SelectItem value="yes">Deleted mails</SelectItem>
                      <SelectItem value="no">Not deleted</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1.5" data-testid="mail-audit-period">
                    <Clock size={13} className="text-amber-700 shrink-0" />
                    <DatePicker
                      value={auditFilters.date_from}
                      onChange={(e) => setAuditFilters((f) => ({ ...f, date_from: e.target.value }))}
                      className="h-9 w-[150px]"
                      placeholder="From"
                      data-testid="mail-audit-date-from"
                    />
                    <span className="text-slate-400 text-xs">–</span>
                    <DatePicker
                      value={auditFilters.date_to}
                      onChange={(e) => setAuditFilters((f) => ({ ...f, date_to: e.target.value }))}
                      className="h-9 w-[150px]"
                      placeholder="To"
                      data-testid="mail-audit-date-to"
                    />
                  </div>
                  <Button variant="ghost" size="sm" className="h-9 px-2 text-slate-500" onClick={() => setAuditFilters({ sender_id: 'all', recipient_id: 'all', subject: '', attachments: 'all', disappearing: 'all', deleted: 'all', date_from: '', date_to: '' })} data-testid="mail-audit-clear">
                    <X size={14} className="mr-1" /> Clear
                  </Button>
                </div>
                {/* Req#1 — quick period presets */}
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <span className="text-[11px] text-amber-700/80 mr-0.5">Quick period:</span>
                  {[7, 30, 90, 180, 365].map((d) => (
                    <Button
                      key={d}
                      variant="outline"
                      size="sm"
                      className="h-7 text-[11px] px-2 bg-white/70"
                      onClick={() => applyAuditPreset(d)}
                      data-testid={`mail-audit-preset-${d}d`}
                    >
                      Last {d}d
                    </Button>
                  ))}
                </div>
                <div className="mt-1.5 text-[11px] text-amber-700 flex items-center gap-1.5">
                  <ShieldAlert size={12} /> Covert audit — senders and recipients are never notified that you viewed their messages.
                </div>
              </div>
            )}

            {/* List body */}
            <ScrollArea className="h-[calc(100vh-320px)] min-h-[360px]">
              {loading ? (
                <div className="divide-y divide-[var(--divider)]" data-testid="notifications-loading">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-3 px-4 py-3">
                      <Skeleton className="h-4 w-4 rounded" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-3.5 w-1/3" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                      <Skeleton className="h-3 w-16" />
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="py-16 text-center" data-testid="notifications-error">
                  <CircleAlert className="mx-auto mb-3 text-rose-500" size={28} />
                  <div className="text-sm text-slate-600 mb-3">Couldn’t load notifications.</div>
                  <Button variant="outline" size="sm" onClick={() => (tab === 'inbox' ? loadInbox() : loadSent())}>Retry</Button>
                </div>
              ) : tab === 'inbox' ? (
                visibleInbox.length === 0 ? (
                  <div className="py-16 text-center" data-testid="notifications-inbox-empty">
                    <Inbox className="mx-auto mb-3 text-slate-300" size={30} />
                    <div className="text-[var(--ink-navy)] font-medium">All caught up</div>
                    <div className="text-sm text-slate-500 mt-1">No notifications match your filters.</div>
                    <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>Clear filters</Button>
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--divider)]" data-testid="notifications-inbox-list">
                    {visibleInbox.map((n) => {
                      const selected = selectedIds.has(n.id);
                      const pUnread = n.priority && !n.read;   // red + bold + pinned
                      return (
                        <div
                          key={n.id}
                          data-testid="notifications-inbox-row"
                          data-priority={n.priority ? '1' : '0'}
                          className={`group grid grid-cols-[24px_1fr_auto] gap-3 items-start px-4 py-3 transition-colors ${
                            selected ? 'bg-[color-mix(in_srgb,var(--diamond-bg)_55%,#fff)]'
                              : pUnread ? 'bg-rose-50/70 hover:bg-rose-50'
                              : !n.read ? 'bg-[color-mix(in_srgb,var(--diamond-bg)_40%,#fff)] hover:bg-[color-mix(in_srgb,var(--diamond-bg)_55%,#fff)]'
                              : 'hover:bg-[var(--paper-warm)]'
                          }`}
                        >
                          <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={selected} onCheckedChange={() => toggleSelect(n.id)} data-testid="notifications-inbox-row-checkbox" aria-label={`Select ${n.title}`} />
                          </div>
                          <button
                            type="button"
                            onClick={() => openInboxItem(n)}
                            className="text-left min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] rounded"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {/* Priority marker — persists forever, even after read. */}
                              {n.priority && <Flag size={13} className={`shrink-0 ${pUnread ? 'text-rose-600' : 'text-rose-500'}`} data-testid="notifications-inbox-row-priority-flag" />}
                              {!n.read && !n.priority && <span className="w-1.5 h-1.5 rounded-full bg-[var(--diamond-icon)] shrink-0" />}
                              <span className={`truncate text-sm ${n.priority ? `text-rose-700 ${!n.read ? 'font-bold' : 'font-normal'}` : !n.read ? 'font-semibold text-[var(--diamond-icon)]' : 'text-slate-800'}`}>
                                {n.sender_name || 'System'}
                              </span>
                              <span className="text-slate-300">·</span>
                              <span className={`truncate text-sm ${n.priority ? `text-rose-700 ${!n.read ? 'font-bold' : 'font-normal'}` : !n.read ? 'font-semibold text-[var(--ink-navy)]' : 'text-slate-700'}`}>{n.title}</span>
                              {n.type === 'internal_message' && <Mail size={12} className="text-slate-400 shrink-0" />}
                              {n.disappearing && (
                                <Badge className="shrink-0 gap-0.5 border border-rose-200 bg-rose-50 text-rose-600 text-[9px] px-1 py-0 font-semibold uppercase tracking-wide" data-testid="notifications-inbox-row-disappearing">
                                  <EyeOff size={9} /> Viewable once
                                </Badge>
                              )}
                              {n.has_attachments && <Paperclip size={12} className="text-slate-400 shrink-0" />}
                            </div>
                            <div className={`text-xs mt-0.5 line-clamp-1 ${n.priority ? `text-rose-700 ${!n.read ? 'font-bold' : 'font-normal'}` : 'text-slate-500'}`}>{n.message}</div>
                            {n.order_no && (
                              <Badge variant="secondary" className="mt-1.5 border border-[var(--diamond-border)] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] text-[10px]" data-testid="notifications-inbox-row-order-chip">
                                {n.order_no}
                              </Badge>
                            )}
                          </button>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <div className="flex items-center gap-0.5 h-7">
                              {/* Rollover quick actions — revealed on hover/focus */}
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-testid="notifications-inbox-row-actions">
                                {!n.read && (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                          onClick={() => markOneRead(n)}
                                          data-testid="notifications-inbox-row-mark-read"
                                          className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-[var(--diamond-bg)] hover:text-[var(--diamond-icon)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                                          aria-label="Mark as read"
                                        >
                                          <MailOpen size={15} />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent>Mark as read</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                <TooltipProvider delayDuration={200}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={() => deleteOne(n)}
                                        data-testid="notifications-inbox-row-delete"
                                        className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                                        aria-label="Delete notification"
                                      >
                                        <Trash2 size={15} />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Delete</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <ChevronRight size={14} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <span className="text-[11px] text-slate-400 font-mono tabular-nums whitespace-nowrap">{formatDateTime(n.created_at)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : tab === 'sent' ? (
                // ---- SENT (comments + internal emails) ----
                visibleSent.length === 0 ? (
                  <div className="py-16 text-center" data-testid="notifications-sent-empty">
                    <Send className="mx-auto mb-3 text-slate-300" size={30} />
                    <div className="text-[var(--ink-navy)] font-medium">Nothing sent yet</div>
                    <div className="text-sm text-slate-500 mt-1">Your @mention comments and internal emails appear here with delivery & read receipts.</div>
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--divider)]" data-testid="notifications-sent-list">
                    {visibleSent.map((s) => (
                      <div key={s.kind === 'email' ? `e-${s.message_id}` : `c-${s.comment_id}`} data-testid="notifications-sent-row" className="group grid grid-cols-[1fr_auto] gap-3 items-start px-4 py-3 hover:bg-[var(--paper-warm)] transition-colors">
                        <button
                          type="button"
                          onClick={() => (s.kind === 'email' ? openViewer(s.message_id, false) : navigate(s.link))}
                          className="text-left min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] rounded"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            {s.priority && <Flag size={13} className="text-rose-500 shrink-0" data-testid="notifications-sent-row-priority-flag" />}
                            {s.kind === 'email' ? (
                              <Badge variant="secondary" className="border border-[var(--diamond-border)] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] text-[10px]"><Mail size={10} className="mr-1" />Email</Badge>
                            ) : (
                              <Badge variant="secondary" className="border border-[var(--diamond-border)] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] text-[10px]">{s.order_no}</Badge>
                            )}
                            {s.kind === 'email' && s.disappearing && (
                              <Badge className="gap-0.5 border border-rose-200 bg-rose-50 text-rose-600 text-[9px] px-1 py-0 font-semibold uppercase tracking-wide" data-testid="notifications-sent-row-disappearing">
                                <EyeOff size={9} /> Viewable once
                              </Badge>
                            )}
                            <div className="flex -space-x-1.5">
                              {(s.recipients || []).slice(0, 3).map((r) => (
                                <InitialsAvatar key={r.user_id} name={r.name} tone={r.read ? 'blue' : 'slate'} className="ring-2 ring-white" />
                              ))}
                              {(s.recipients || []).length > 3 && (
                                <span className="inline-flex items-center justify-center h-7 px-1.5 rounded-full border bg-slate-100 text-slate-600 text-[10px] font-semibold ring-2 ring-white">
                                  +{s.recipients.length - 3}
                                </span>
                              )}
                            </div>
                            <span className="text-[11px] text-slate-400">{s.read_count}/{s.recipient_count} read</span>
                            {s.has_attachments && <Paperclip size={12} className="text-slate-400" />}
                          </div>
                          <div className="text-sm text-slate-700 mt-1 line-clamp-1">
                            {s.kind === 'email' ? (s.subject || '(no subject)') : (s.body || '(no text)')}
                          </div>
                        </button>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <div className="flex items-center gap-2">
                            <TickIcon state={s.tick} />
                            {s.kind === 'comment' ? (
                              <button type="button" onClick={() => openReceipt(s)} className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-[var(--ink-navy)]" data-testid="notifications-sent-row-receipt-eye" title="View receipt timeline">
                                <Eye size={15} />
                              </button>
                            ) : (
                              <button type="button" onClick={() => openViewer(s.message_id, false)} className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-[var(--ink-navy)]" data-testid="notifications-sent-row-open" title="Open message">
                                <Eye size={15} />
                              </button>
                            )}
                          </div>
                          <span className="text-[11px] text-slate-400 font-mono tabular-nums whitespace-nowrap">{formatDateTime(s.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : tab === 'priority' ? (
                // ---- PRIORITY FOLDER (received + sent; emails + comments) ----
                (priorityData.received.length === 0 && priorityData.sent.length === 0) ? (
                  <div className="py-16 text-center" data-testid="notifications-priority-empty">
                    <Flag className="mx-auto mb-3 text-rose-300" size={30} />
                    <div className="text-[var(--ink-navy)] font-medium">No priority messages</div>
                    <div className="text-sm text-slate-500 mt-1">Priority emails and comments (received & sent) collect here. They can be deleted but never un-prioritised.</div>
                  </div>
                ) : (
                  <div data-testid="notifications-priority-list">
                    {['received', 'sent'].map((dir) => (priorityData[dir].length > 0 && (
                      <div key={dir}>
                        <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-rose-600 bg-rose-50/60 border-b border-[var(--divider)]">{dir === 'received' ? 'Received' : 'Sent'}</div>
                        <div className="divide-y divide-[var(--divider)]">
                          {priorityData[dir].map((it, idx) => (
                            <div key={`${dir}-${it.message_id || it.comment_id || it.notif_id || idx}`} data-testid="notifications-priority-row" className="group grid grid-cols-[1fr_auto] gap-3 items-start px-4 py-3 hover:bg-rose-50/40 transition-colors">
                              <button
                                type="button"
                                onClick={() => (it.kind === 'email' && it.message_id ? openViewer(it.message_id, false) : (it.link ? navigate(it.link) : null))}
                                className="text-left min-w-0 focus:outline-none rounded"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <Flag size={13} className="text-rose-500 shrink-0" />
                                  <Badge variant="secondary" className="border border-[var(--diamond-border)] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] text-[10px]">
                                    {it.kind === 'email' ? <><Mail size={10} className="mr-1" />Email</> : (it.order_no || 'Comment')}
                                  </Badge>
                                  <span className="truncate text-sm font-medium text-rose-700">{it.subject || it.title}</span>
                                  {it.has_attachments && <Paperclip size={12} className="text-slate-400 shrink-0" />}
                                </div>
                                <div className="text-xs text-rose-600 mt-0.5 line-clamp-1">{it.message}</div>
                              </button>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => deletePriorityItem(it)}
                                  className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors opacity-0 group-hover:opacity-100"
                                  data-testid="notifications-priority-row-delete"
                                  title="Delete from priority"
                                >
                                  <Trash2 size={15} />
                                </button>
                                <span className="text-[11px] text-slate-400 font-mono tabular-nums whitespace-nowrap">{formatDateTime(it.created_at)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )))}
                  </div>
                )
              ) : (
                // ---- SUPER ADMIN INTERNAL MAIL AUDIT ----
                auditRows.length === 0 ? (
                  <div className="py-16 text-center" data-testid="notifications-audit-empty">
                    <ShieldAlert className="mx-auto mb-3 text-amber-300" size={30} />
                    <div className="text-[var(--ink-navy)] font-medium">No internal mail</div>
                    <div className="text-sm text-slate-500 mt-1">No messages match your filters.</div>
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--divider)]" data-testid="notifications-audit-list">
                    {auditRows.map((m) => (
                      <div
                        key={m.message_id}
                        data-testid="notifications-audit-row"
                        className="group grid grid-cols-[1fr_auto] gap-3 items-start px-4 py-3 hover:bg-amber-50/50 transition-colors"
                      >
                        <button
                          type="button"
                          onClick={() => openViewer(m.message_id, true)}
                          className="text-left min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] rounded"
                          data-testid="notifications-audit-row-open"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-wrap">
                            {m.priority && <Flag size={13} className="text-rose-500 shrink-0" />}
                            <span className={`truncate text-sm font-medium ${m.priority ? 'text-rose-700' : 'text-[var(--ink-navy)]'}`}>{m.subject}</span>
                            {m.has_attachments && <Paperclip size={12} className="text-slate-400 shrink-0" />}
                            {/* Disappearing — amber while any recipient is unread, green once all have read it. */}
                            {m.disappearing && (
                              <Badge
                                className={`gap-0.5 text-[9px] px-1 py-0 font-semibold uppercase tracking-wide border ${
                                  m.all_read
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                    : 'border-amber-200 bg-amber-50 text-amber-700'
                                }`}
                                data-testid="notifications-audit-row-disappearing"
                              >
                                <EyeOff size={9} /> Disappearing{m.all_read ? ' · all read' : ''}
                              </Badge>
                            )}
                            {/* Deleted-by-sender — amber chip with a hover timeline. */}
                            {m.deleted_for_all && (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge className="gap-0.5 text-[9px] px-1 py-0 font-semibold uppercase tracking-wide border border-amber-200 bg-amber-50 text-amber-700 cursor-help" data-testid="notifications-audit-row-deleted">
                                      <Trash2 size={9} /> Deleted by sender
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-xs leading-relaxed">
                                      <div className="font-semibold">Deleted for everyone</div>
                                      <div>by {m.deleted_for_all_by_name || m.sender?.name || 'sender'}</div>
                                      <div className="font-mono tabular-nums">{m.deleted_for_all_at ? formatDateTime(m.deleted_for_all_at) : '—'}</div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 truncate">
                            <span className="font-medium text-slate-600">{m.sender?.name}</span>
                            <span className="text-slate-300"> → </span>
                            {(m.recipients || []).map((r) => r.name).join(', ')}
                          </div>
                        </button>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <div className="flex items-center gap-1">
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => setHardDelete(m)}
                                    className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                                    data-testid="notifications-audit-row-hard-delete"
                                    aria-label="Hard delete without a trace"
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Hard delete — erase without a trace</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <span className="text-[11px] text-slate-500">{m.read_count}/{m.recipient_count} read</span>
                          </div>
                          <span className="text-[11px] text-slate-400 font-mono tabular-nums whitespace-nowrap">{formatDateTime(m.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </ScrollArea>
          </Card>
          )}
        </div>
      </div>

      {/* Receipt timeline dialog */}
      <Dialog open={!!receipt} onOpenChange={(v) => { if (!v) setReceipt(null); }}>
        <DialogContent className="max-w-lg" data-testid="notifications-receipt-dialog">
          <DialogHeader>
            <DialogTitle>Read receipts</DialogTitle>
            <DialogDescription>
              {receipt ? <>Comment on <span className="font-medium text-[var(--ink-navy)]">{receipt.order_no}</span> · sent {formatDateTime(receipt.created_at)}</> : ''}
            </DialogDescription>
          </DialogHeader>
          {receipt && (
            <>
              <div className="rounded-lg bg-[var(--paper-warm)] border border-[var(--divider)] px-3 py-2 text-sm text-slate-700 line-clamp-3">
                {receipt.body || '(no text)'}
              </div>
              <div className="mt-2 space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                {(receipt.recipients || []).map((r) => (
                  <div key={r.user_id} className="flex items-start gap-3" data-testid={`notifications-receipt-recipient-${r.user_id}`}>
                    <InitialsAvatar name={r.name} tone={r.read ? 'blue' : 'slate'} />
                    <div className="flex-1 min-w-0 border-l border-[var(--divider)] pl-3 -ml-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-[var(--ink-navy)] truncate">{r.name}</span>
                        <TickIcon state={r.tick} size={15} />
                      </div>
                      <div className="mt-1 space-y-1">
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`w-1.5 h-1.5 rounded-full ${r.delivered ? 'bg-[var(--diamond-icon)]' : 'bg-slate-300'}`} />
                          <CheckCheck size={12} className={r.delivered ? 'text-[var(--diamond-icon)]' : 'text-slate-300'} />
                          <span className="text-slate-600">Delivered</span>
                          <span className="text-slate-400 font-mono tabular-nums">{r.delivered_at ? formatDateTime(r.delivered_at) : '—'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`w-1.5 h-1.5 rounded-full ${r.read ? 'bg-[var(--diamond-icon)]' : 'bg-slate-300'}`} />
                          <Eye size={12} className={r.read ? 'text-[var(--diamond-icon)]' : 'text-slate-300'} />
                          <span className="text-slate-600">Opened</span>
                          <span className="text-slate-400 font-mono tabular-nums">{r.read_at ? formatDateTime(r.read_at) : '—'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {(receipt.recipients || []).length === 0 && (
                  <div className="text-sm text-slate-500 text-center py-4">No recipients.</div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Super Admin — no-trace hard delete confirmation */}
      <AlertDialog open={!!hardDelete} onOpenChange={(v) => { if (!v) setHardDelete(null); }}>
        <AlertDialogContent data-testid="mail-hard-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Erase this message without a trace?</AlertDialogTitle>
            <AlertDialogDescription>
              {hardDelete ? (
                <>This permanently deletes <span className="font-medium text-[var(--ink-navy)]">“{hardDelete.subject}”</span> and every related inbox &amp; read-receipt record. It cannot be recovered and leaves no audit trail. This action is irreversible.</>
              ) : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={hardDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={(e) => { e.preventDefault(); doHardDelete(); }} disabled={hardDeleting} data-testid="mail-hard-delete-confirm-button">
              {hardDeleting ? 'Erasing…' : 'Hard delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Internal mail — message viewer (compose is provided globally) */}
      <MessageViewerDialog
        open={viewer.open}
        messageId={viewer.messageId}
        admin={viewer.admin}
        onClose={closeViewer}
        onReply={(m) => { closeViewer(); replyTo(m); }}
        onReplyAll={(m) => { closeViewer(); replyAll(m); }}
        onDeleted={onMailChanged}
      />

      {/* Daily Digest — company-wise summary viewer */}
      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="notifications-digest-dialog">
          <DialogHeader>
            <DialogTitle>{digestNotif?.title || 'Daily Digest'}</DialogTitle>
          </DialogHeader>
          <DigestView payload={digestNotif?.digest} />
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
